import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Mail, ArrowLeft, RefreshCw, Check, AlertCircle } from 'lucide-react';

export default function ForgotPassword() {
  const { currentColors } = useTheme();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (error) {
      if (error.response?.status === 429) {
        setError('Too many requests. Please try again later.');
      } else {
        // Still show success to prevent email enumeration
        setSuccess(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center p-4">
      <div 
        className="w-full max-w-md rounded-2xl p-8 backdrop-blur-xl"
        style={{ 
          backgroundColor: `${currentColors.background}cc`,
          border: `1px solid ${currentColors.border}`
        }}
      >
        <Link 
          to="/"
          className="flex items-center gap-2 text-sm mb-6 hover:opacity-80"
          style={{ color: currentColors.textMuted }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back to login
        </Link>

        <div className="text-center mb-8">
          <div 
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${currentColors.primary}20` }}
          >
            <Mail className="w-8 h-8" style={{ color: currentColors.primary }} />
          </div>
          <h1 className="text-2xl font-cinzel mb-2" style={{ color: currentColors.primary }}>
            Forgot Password
          </h1>
          <p className="text-sm" style={{ color: currentColors.textMuted }}>
            {success 
              ? "Check your email for reset instructions"
              : "Enter your email and we'll send you a reset link"
            }
          </p>
        </div>

        {success ? (
          <div className="space-y-4">
            <div 
              className="p-4 rounded-xl flex items-start gap-3"
              style={{ backgroundColor: 'rgba(34, 197, 94, 0.1)' }}
            >
              <Check className="w-5 h-5 text-green-500 mt-0.5" />
              <div className="text-sm">
                <p className="text-green-500 font-medium">Email Sent!</p>
                <p style={{ color: currentColors.textMuted }}>
                  If an account exists with <strong>{email}</strong>, you will receive a password 
                  reset link shortly.
                </p>
              </div>
            </div>
            
            <div 
              className="p-4 rounded-xl"
              style={{ backgroundColor: `${currentColors.accent}10` }}
            >
              <p className="text-sm" style={{ color: currentColors.textMuted }}>
                <strong style={{ color: currentColors.accent }}>Demo Mode:</strong> Check the 
                server console for the reset link. In production, this would be sent via email.
              </p>
            </div>
            
            <Link
              to="/"
              className="cosmic-btn w-full flex items-center justify-center gap-2"
            >
              Return to Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}
            
            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="cosmic-input w-full"
                required
                autoFocus
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="cosmic-btn w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Send Reset Link
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
